import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { CountBadge } from '../UI/CountBadge';

// ---------------------------------------------------------------------------
// Priority map — FALLBACK only.
//
// The canonical source of truth is ``NotificationService.TYPE_TO_PRIORITY``
// in ``staff_core/services/notification_service.py`` — exposed on the
// ``/admin/notification-policies/`` and ``/admin/notification-channel-defaults/``
// API responses as ``type_to_priority``. The matrix prefers the API value and
// only falls back to this static map when the response is missing or stale
// (e.g. cached HTML from a previous deploy). Keep it loosely aligned with
// the backend so the fallback is sensible, but DO NOT rely on it as the
// authoritative source — drift here is recoverable, drift in the backend
// is not.
//
// Audit drift any time with ``scripts/_audit_notification_frontend_backend_sync.py``.
// ---------------------------------------------------------------------------
const FALLBACK_TYPE_TO_PRIORITY = {
  leave_approved: 'medium',
  leave_rejected: 'high',
  leave_pending_approval: 'medium',
  leave_escalated: 'high',
  leave_cancelled_notice: 'medium',
  leave_pending_lapsed: 'urgent',
  leave_lapsed_ro_alert: 'urgent',
  leave_lapsed_hr_alert: 'high',
  leave_digest: 'high',
  leave_approval_required: 'high',
  oil_credited: 'medium',
  oil_expiring: 'medium',
  oil_forfeited: 'medium',
  al_planning_reminder: 'medium',
  al_forfeiture_warning: 'high',

  missing_clockin: 'high',
  missing_clockin_pm: 'high',
  clockout_soft_reminder: 'low',
  clockout_urgent_reminder: 'medium',
  clockout_final_warning: 'high',
  exception_flagged: 'medium',
  exception_resolved: 'low',

  document_request_approved: 'medium',
  document_request_rejected: 'high',
  document_request_cancelled: 'low',
  hr_letter_ready: 'medium',
  certificate_expiring: 'medium',
  document_approval_update: 'medium',

  payslip_available: 'medium',

  overtime_at_risk: 'medium',
  overtime_critical: 'high',
  overtime_exceeded: 'urgent',
  overtime_planning_digest: 'medium',
  rest_day_watch: 'low',
  rest_day_warning: 'medium',
  rest_day_critical: 'high',
  rest_day_violated: 'urgent',

  probation_ending: 'medium',
  contract_ending: 'high',
  onboarding_reminder: 'medium',
  offboarding_reminder: 'medium',

  system_maintenance: 'low',
  policy_update: 'low',
  general_announcement: 'low',
  hr_admin_digest: 'high',
  work_pass_decision_action: 'high',
  work_pass_decision_digest: 'high',
  work_pass_escalation: 'high',

  asset_return_pending: 'medium',
  asset_return_confirmed: 'medium',
  asset_return_rejected: 'high',
  asset_return_cancelled: 'low',
  asset_return_expired: 'medium',
  asset_transfer_incoming: 'high',
  asset_transfer_accepted: 'medium',
  asset_transfer_rejected: 'medium',
  asset_transfer_cancelled: 'low',
  asset_transfer_expired: 'high',
  asset_receipt_pending: 'high',
  asset_receipt_acknowledged: 'medium',

  nightwork_submitted: 'medium',
  nightwork_approved: 'medium',
  nightwork_fully_approved: 'high',
  nightwork_rejected: 'high',
  nightwork_cancelled: 'high',
  nightwork_assigned: 'high',
  nightwork_amended: 'medium',

  restday_work_submitted: 'medium',
  restday_work_approved: 'medium',
  restday_work_fully_approved: 'high',
  restday_work_rejected: 'high',
  restday_work_cancelled: 'high',
  restday_work_assigned: 'high',
  restday_work_amended: 'medium',

  birthday_wishes: 'low',
};

const resolvePriority = (typeToPriority, slug) => {
  if (typeToPriority && typeToPriority[slug]) return typeToPriority[slug];
  if (FALLBACK_TYPE_TO_PRIORITY[slug]) return FALLBACK_TYPE_TO_PRIORITY[slug];
  return 'medium';
};

const PRIORITY_STYLES = {
  urgent: { badge: 'bg-red-100 text-red-700 border border-red-200',   dot: 'bg-red-500',   label: 'Urgent' },
  high:   { badge: 'bg-amber-100 text-amber-700 border border-amber-200', dot: 'bg-amber-500', label: 'High' },
  medium: { badge: 'bg-blue-100 text-blue-700 border border-blue-200',  dot: 'bg-blue-400',  label: 'Medium' },
  low:    { badge: 'bg-gray-100 text-gray-500 border border-gray-200',  dot: 'bg-gray-400',  label: 'Low' },
};

// ---------------------------------------------------------------------------
// NotificationTypeReferencePanel — read-only catalogue of all live types.
// Data (labels, categories) comes from the same API as the policy matrix.
// ---------------------------------------------------------------------------
function NotificationTypeReferencePanel({ notificationTypes, typeToCategory, typeToPriority, categoryLabels, categoryOrder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterPriority, setFilterPriority] = useState('');

  if (!notificationTypes || notificationTypes.length === 0) return null;

  // Group by category, optionally filtered by priority. Priority is sourced
  // from the API (typeToPriority) so adding a new notification type on the
  // backend automatically renders the correct chip here without a frontend
  // deploy. FALLBACK_TYPE_TO_PRIORITY is consulted only when the API value
  // is missing (e.g. stale cached HTML from a previous deploy).
  const grouped = {};
  categoryOrder.forEach(cat => { grouped[cat] = []; });

  notificationTypes.forEach(t => {
    const cat = typeToCategory[t.value] || 'system';
    const priority = resolvePriority(typeToPriority, t.value);
    if (filterPriority && priority !== filterPriority) return;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ ...t, priority });
  });

  const totalShown = Object.values(grouped).reduce((n, arr) => n + arr.length, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header / toggle */}
      <button
        onClick={() => setIsOpen(p => !p)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`w-5 h-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Notification Type Reference</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Read-only catalogue of all {notificationTypes.length} live notification types — their category and default priority.
            </p>
          </div>
        </div>
        <CountBadge count={notificationTypes.length} theme="admin" size="md" className="flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="border-t border-gray-100">
          {/* Filter bar */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-3">
            <span className="text-xs font-medium text-gray-500">Filter by priority:</span>
            <div className="flex gap-2">
              {['', 'urgent', 'high', 'medium', 'low'].map(p => (
                <button
                  key={p}
                  onClick={() => setFilterPriority(p)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    filterPriority === p
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {p === '' ? 'All' : PRIORITY_STYLES[p]?.label}
                </button>
              ))}
            </div>
            {filterPriority && (
              <span className="ml-auto text-xs text-gray-400">{totalShown} of {notificationTypes.length} shown</span>
            )}
          </div>

          {/* Type rows grouped by category */}
          <div className="divide-y divide-gray-200">
            {categoryOrder.map(cat => {
              const types = grouped[cat];
              if (!types || types.length === 0) return null;
              return (
                <div key={cat}>
                  <div className="px-6 py-2 bg-gray-50/60">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {categoryLabels[cat] || cat}
                    </span>
                  </div>
                  {types.map((type, idx) => {
                    const ps = PRIORITY_STYLES[type.priority] || PRIORITY_STYLES.medium;
                    return (
                      <div
                        key={type.value}
                        className={`flex items-center justify-between px-6 py-2.5 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${ps.dot}`} />
                          <span className="text-sm text-gray-700">{type.label}</span>
                          <span className="text-xs text-gray-400 font-mono hidden sm:block">{type.value}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ml-3 ${ps.badge}`}>
                          {ps.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer note */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Priorities shown are system defaults. To suppress a type entirely for a classification, use the policy matrix above.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// RO Digest Settings sub-component
// ---------------------------------------------------------------------------

function RODigestSettings() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get('/admin/ro-notification-config/');
      setConfig(res.data);
      setError(null);
    } catch {
      setError('Failed to load RO digest settings');
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const updateField = async (field, value) => {
    setSaving(prev => ({ ...prev, [field]: true }));
    const optimistic = { ...config, [field]: value };
    setConfig(optimistic);
    try {
      const res = await api.put('/admin/ro-notification-config/1/', { [field]: value });
      setConfig(res.data);
    } catch {
      fetchConfig();
    } finally {
      setSaving(prev => ({ ...prev, [field]: false }));
    }
  };

  const updateTime = async (value) => {
    if (!/^\d{2}:\d{2}$/.test(value)) return;
    setSaving(prev => ({ ...prev, absence_digest_send_time: true }));
    try {
      const res = await api.put('/admin/ro-notification-config/1/', { absence_digest_send_time: value });
      setConfig(res.data);
    } catch {
      fetchConfig();
    } finally {
      setSaving(prev => ({ ...prev, absence_digest_send_time: false }));
    }
  };

  if (!config && !error) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-600" />
        <span className="ml-2 text-sm text-gray-500">Loading RO digest settings…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {error} — <button onClick={fetchConfig} className="underline">Retry</button>
      </div>
    );
  }

  const rows = [
    {
      field: 'ro_leave_batch_always',
      label: 'Batch Leave Approval Notifications',
      description:
        'All leave-pending-approval notifications to Reporting Officers are queued for the morning digest, even for urgent (within 2 working days) leave applications. Eliminates per-application spam for ROs managing large teams.',
      value: config.ro_leave_batch_always,
    },
    {
      field: 'ro_absence_batch',
      label: 'Batch Absence Escalation Notifications',
      description:
        'When multiple team members are unaccounted for, the Reporting Officer receives one consolidated notification instead of a separate alert per employee. Delivered at the send time configured below.',
      value: config.ro_absence_batch,
    },
  ];

  return (
    <div className="divide-y divide-gray-100">
      {rows.map(({ field, label, description, value }) => (
        <div key={field} className="flex items-start justify-between px-6 py-4 table-row">
          <div className="flex-1 pr-6">
            <p className="text-sm font-medium text-gray-800">{label}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
          </div>
          <button
            onClick={() => updateField(field, !value)}
            disabled={!!saving[field]}
            className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500 ${
              value ? 'bg-green-500' : 'bg-gray-300'
            } ${saving[field] ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
            title={value ? 'Enabled — click to disable' : 'Disabled — click to enable'}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                value ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      ))}

      {/* Absence digest send time — only relevant when batch mode is on */}
      <div className={`flex items-start justify-between px-6 py-4 transition-colors ${
        config.ro_absence_batch ? 'table-row' : 'opacity-40 pointer-events-none bg-gray-50/50'
      }`}>
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">Absence Digest Send Time</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Time (SGT, 24-hour) at which the consolidated absence digest is sent to each
            Reporting Officer. The task checks in 15-minute windows — choose a time after
            the last employee clock-in check (12:30 PM recommended). Only applies when
            batch absence escalation is enabled above.
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          <input
            type="time"
            defaultValue={config.absence_digest_send_time}
            onBlur={e => updateTime(e.target.value)}
            disabled={!config.ro_absence_batch || !!saving.absence_digest_send_time}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {saving.absence_digest_send_time && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-500" />
          )}
        </div>
      </div>

      {config.updated_at && (
        <div className="px-6 py-3 text-xs text-gray-400 bg-gray-50/50">
          Last updated{config.updated_by ? ` by ${config.updated_by}` : ''} ·{' '}
          {new Date(config.updated_at).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OvertimeDigestStatus — read-only operational status for the weekly 72h
// overtime planning digest.
//
// This card is intentionally read-only: the master kill-switch
// (OVERTIME_NOTIFICATIONS_ENABLED) lives in .env so a deploy log entry
// provides the audit trail (matches the WHATSAPP_ENABLED pattern). HR can
// still preview via the management command — the surface here is purely
// informational so the team can see at a glance whether the next Monday
// 08:00 SGT digest will go out, who will receive the rollup, and how the
// last run finished.
// ---------------------------------------------------------------------------
function OvertimeDigestStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/overtime-digest-status/');
      setStatus(res.data);
      setError(null);
    } catch {
      setError('Failed to load overtime digest status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-600" />
        <span className="ml-2 text-sm text-gray-500">Loading overtime digest status…</span>
      </div>
    );
  }

  if (error || !status) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 m-4">
        {error || 'No status available'} — <button onClick={fetchStatus} className="underline">Retry</button>
      </div>
    );
  }

  const enabled = !!status.enabled;
  const lastRun = status.last_run;
  const formatRelative = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const lastRunStatusColor = (taskStatus) => {
    if (taskStatus === 'SUCCESS') return 'text-green-700 bg-green-50 border-green-200';
    if (taskStatus === 'FAILURE') return 'text-red-700 bg-red-50 border-red-200';
    if (taskStatus === 'RETRY') return 'text-amber-700 bg-amber-50 border-amber-200';
    if (taskStatus === 'STARTED' || taskStatus === 'PENDING') return 'text-blue-700 bg-blue-50 border-blue-200';
    return 'text-gray-700 bg-gray-50 border-gray-200';
  };

  return (
    <div className="divide-y divide-gray-100">
      {/* Status row */}
      <div className="flex items-start justify-between px-6 py-4">
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">Status</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Master kill-switch for the weekly digest. Managed via
            <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-[11px]">OVERTIME_NOTIFICATIONS_ENABLED</code>
            in <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-[11px]">.env</code>
            — a deploy log entry provides the audit trail (matches the WhatsApp pattern).
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
          enabled
            ? 'bg-green-50 text-green-700 border-green-200'
            : 'bg-gray-50 text-gray-600 border-gray-200'
        }`}>
          {enabled ? '● Enabled' : '○ Disabled'}
        </span>
      </div>

      {/* Schedule row */}
      <div className="flex items-start justify-between px-6 py-4">
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">Schedule</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            When the consolidated forecast-based digest is sent to HOD / Deputy HOD /
            Trade Leader / RO and the HR rollup. Runs even when no forecasts trigger
            so failures surface promptly.
          </p>
        </div>
        <span className="text-sm text-gray-700 font-medium">{status.schedule_label}</span>
      </div>

      {/* In-scope departments row */}
      <div className="flex items-start justify-between px-6 py-4">
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">In-Scope Departments</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Departments covered by the overtime digest. Resolved by UUID at
            startup so admin renames don't silently drop workers from the
            digest.
          </p>
          {status.in_scope_resolution_error && (
            <p className="text-xs text-red-600 mt-1 leading-relaxed">
              ⚠ {status.in_scope_resolution_error}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end max-w-[50%]">
          {(status.in_scope_departments || []).map(d => (
            <span
              key={d.name}
              className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                d.department_id
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
              title={d.department_id ? `UUID: ${d.department_id}` : 'Not resolved'}
            >
              {d.name}
            </span>
          ))}
        </div>
      </div>

      {/* HR rollup recipients row */}
      <div className="flex items-start justify-between px-6 py-4">
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">HR Rollup Recipients</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Active staff in the HR group (or with admin access) who have a work
            email. They receive the cross-department rollup of every worker at
            <span className="font-medium"> watch </span> tier and above.
          </p>
        </div>
        <span className="text-sm text-gray-700 font-medium">
          {status.hr_recipient_count != null ? `${status.hr_recipient_count} recipient${status.hr_recipient_count === 1 ? '' : 's'}` : '—'}
        </span>
      </div>

      {/* Webhook + channel default health */}
      <div className="flex items-start justify-between px-6 py-4">
        <div className="flex-1 pr-6">
          <p className="text-sm font-medium text-gray-800">Plumbing Health</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
            Both indicators must be green for the digest to leave the building on
            Monday morning.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 text-xs">
          <span className={`inline-flex items-center gap-1.5 ${
            status.webhook_configured ? 'text-green-700' : 'text-red-700'
          }`}>
            <span className={`inline-block w-2 h-2 rounded-full ${status.webhook_configured ? 'bg-green-500' : 'bg-red-500'}`} />
            n8n email webhook {status.webhook_configured ? 'configured' : 'missing'}
          </span>
          <span className={`inline-flex items-center gap-1.5 ${
            status.channel_default_present ? 'text-green-700' : 'text-red-700'
          }`}>
            <span className={`inline-block w-2 h-2 rounded-full ${status.channel_default_present ? 'bg-green-500' : 'bg-red-500'}`} />
            Channel default row {status.channel_default_present ? 'seeded' : 'missing'}
          </span>
        </div>
      </div>

      {/* Last run row */}
      <div className="px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-6">
            <p className="text-sm font-medium text-gray-800">Last Run</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Most recent execution recorded in CeleryTaskExecution. Per-recipient
              email delivery is tracked separately under the
              <code className="mx-1 px-1 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-[10px]">send_notification_email_webhook</code>
              task.
            </p>
          </div>
          <div className="flex-shrink-0 text-right">
            {lastRun ? (
              <>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${lastRunStatusColor(lastRun.status)}`}>
                  {lastRun.status}
                </span>
                <p className="text-xs text-gray-500 mt-1">
                  {formatRelative(lastRun.completed_at || lastRun.started_at)}
                </p>
              </>
            ) : (
              <span className="text-xs text-gray-400 italic">Never run on this environment</span>
            )}
          </div>
        </div>
        {lastRun && lastRun.summary && (
          <p className="mt-3 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 font-mono leading-relaxed">
            {lastRun.summary}
          </p>
        )}
      </div>
    </div>
  );
}

const CATEGORY_LABELS = {
  leave: 'Leave & Time-Off',
  attendance: 'Attendance',
  documents: 'Documents & Certificates',
  payroll: 'Payroll',
  compliance: 'Compliance Alerts',
  employment: 'Employment Status',
  nightwork: 'Nightwork',
  restday_work: 'Rest Day Work',
  assets: 'Assets',
  system: 'System Announcements',
  celebrations: 'Celebrations',
};

const CLASSIFICATION_ORDER = [
  'managing_director',
  'executive_director',
  'staff',
  'worker',
  'intern',
];

const CLASSIFICATION_LABELS = {
  managing_director: 'MD',
  executive_director: 'ED',
  staff: 'Staff',
  worker: 'Worker',
  intern: 'Intern',
};

const CLASSIFICATION_FULL_LABELS = {
  managing_director: 'Managing Director',
  executive_director: 'Executive Director',
  staff: 'Staff',
  worker: 'Worker',
  intern: 'Intern',
};

// ---------------------------------------------------------------------------
// Channel Delivery Defaults — admin toggles for system-wide channel ON/OFF
// and locked (employee-override-proof) flags per notification type.
// ---------------------------------------------------------------------------

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'email', label: 'Email' },
  { key: 'google_chat', label: 'Google Chat' },
];

function ChannelDeliveryDefaults({ categoryOrder }) {
  const [defaults, setDefaults] = useState([]);
  const [typeMeta, setTypeMeta] = useState([]);
  const [typeToCategory, setTypeToCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchDefaults = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/notification-channel-defaults/');
      setDefaults(res.data.defaults || []);
      setTypeMeta(res.data.notification_types || []);
      setTypeToCategory(res.data.type_to_category || {});
      const cats = {};
      (res.data.notification_categories || []).forEach(c => { cats[c.value] = false; });
      setExpandedCategories(cats);
      setError(null);
    } catch {
      setError('Failed to load channel defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDefaults(); }, [fetchDefaults]);

  const getRow = (notificationType) => defaults.find(d => d.notification_type === notificationType);

  const toggleField = async (notificationType, field, currentValue) => {
    const key = `${notificationType}:${field}`;
    setSaving(prev => ({ ...prev, [key]: true }));

    // Optimistic update
    setDefaults(prev =>
      prev.map(d =>
        d.notification_type === notificationType ? { ...d, [field]: !currentValue } : d
      )
    );

    try {
      const res = await api.put(`/admin/notification-channel-defaults/${notificationType}/`, {
        [field]: !currentValue,
      });
      setDefaults(prev =>
        prev.map(d => (d.notification_type === notificationType ? res.data : d))
      );
    } catch {
      fetchDefaults();
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleExpanded = (cat) =>
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));

  // Group types by category
  const grouped = {};
  typeMeta.forEach(t => {
    const cat = typeToCategory[t.value] || 'system';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-600" />
          <span className="text-sm text-gray-500">Loading channel delivery defaults…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {error} — <button onClick={fetchDefaults} className="underline">Retry</button>
      </div>
    );
  }

  return (
    <>
      {/* Section header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Channel Delivery Defaults
        </h3>
        <p className="text-sm text-gray-500">
          Set the system-wide default delivery channels for each notification type.
          Toggle a channel ON or OFF, and use the lock icon to prevent employees from overriding
          your choice — locked channels enforce the admin default for all employees.
        </p>
      </div>

      {/* Matrix by category */}
      {(categoryOrder || Object.keys(grouped)).map(category => {
        const types = grouped[category];
        if (!types || types.length === 0) return null;
        const isExpanded = expandedCategories[category];
        const label = CATEGORY_LABELS[category] || category;

        return (
          <div key={`cd-${category}`} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleExpanded(category)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900">{label}</h4>
                <CountBadge count={types.length} theme="admin" size="sm" />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="table table-fixed w-full min-w-[640px]">
                  <colgroup>
                    <col />
                    <col className="w-[140px]" />
                    <col className="w-[140px]" />
                    <col className="w-[140px]" />
                  </colgroup>
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Notification Type</th>
                      {CHANNELS.map(ch => (
                        <th key={ch.key} className="table-header-cell text-center">
                          <span className="block">{ch.label}</span>
                          <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">
                            Default / Lock
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {types.map((type) => {
                      const row = getRow(type.value);
                      if (!row) return null;
                      return (
                        <tr key={type.value} className="table-row">
                          <td className="table-cell">
                            <span className="text-sm text-gray-700">{type.label}</span>
                          </td>
                          {CHANNELS.map(ch => {
                            const channelOn = row[ch.key];
                            const isLocked = row[`locked_${ch.key}`];
                            const toggleKey = `${type.value}:${ch.key}`;
                            const lockKey = `${type.value}:locked_${ch.key}`;
                            const isSavingToggle = saving[toggleKey];
                            const isSavingLock = saving[lockKey];

                            return (
                              <td key={ch.key} className="table-cell text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => toggleField(type.value, ch.key, channelOn)}
                                    disabled={isSavingToggle}
                                    className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500 ${
                                      channelOn ? 'bg-green-500' : 'bg-gray-300'
                                    } ${isSavingToggle ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                    title={channelOn ? `${ch.label} ON — click to turn OFF` : `${ch.label} OFF — click to turn ON`}
                                  >
                                    <span
                                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                                        channelOn ? 'translate-x-5' : 'translate-x-0'
                                      }`}
                                    />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => toggleField(type.value, `locked_${ch.key}`, isLocked)}
                                    disabled={isSavingLock}
                                    className={`p-1 rounded transition-colors ${
                                      isLocked
                                        ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                                        : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                                    } ${isSavingLock ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                    title={isLocked ? 'Locked — employees cannot override. Click to unlock.' : 'Unlocked — employees can change. Click to lock.'}
                                  >
                                    {isLocked ? (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Legend for this section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-8 h-4 rounded-full bg-green-500" /> Channel ON
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-8 h-4 rounded-full bg-gray-300" /> Channel OFF
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Locked — employees cannot override
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
            </svg>
            Unlocked — employees can change
          </div>
          <div className="ml-auto text-gray-400">
            Changes take effect immediately (cached for up to 5 minutes)
          </div>
        </div>
      </div>
    </>
  );
}

export default function NotificationPolicyMatrix() {
  const [policies, setPolicies] = useState([]);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [typeToCategory, setTypeToCategory] = useState({});
  const [typeToPriority, setTypeToPriority] = useState({});
  const [classificationCounts, setClassificationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/notification-policies/');
      const data = res.data;
      setPolicies(data.policies || []);
      setNotificationTypes(data.notification_types || []);
      setTypeToCategory(data.type_to_category || {});
      setTypeToPriority(data.type_to_priority || {});
      setClassificationCounts(data.classification_counts || {});

      const cats = {};
      (data.notification_categories || []).forEach(c => { cats[c.value] = true; });
      setExpandedCategories(cats);
      setError(null);
    } catch (err) {
      setError('Failed to load notification policies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const getDisabledSet = (classification) => {
    const p = policies.find(p => p.employment_classification === classification);
    return new Set(p?.disabled_types || []);
  };

  const toggleType = async (classification, notificationType) => {
    const key = `${classification}:${notificationType}`;
    setSaving(prev => ({ ...prev, [key]: true }));

    const disabled = getDisabledSet(classification);
    let newDisabled;
    if (disabled.has(notificationType)) {
      newDisabled = [...disabled].filter(t => t !== notificationType);
    } else {
      newDisabled = [...disabled, notificationType];
    }

    // Optimistic update
    setPolicies(prev =>
      prev.map(p =>
        p.employment_classification === classification
          ? { ...p, disabled_types: newDisabled }
          : p
      )
    );

    try {
      await api.put(`/admin/notification-policies/${classification}/`, {
        disabled_types: newDisabled,
      });
    } catch (err) {
      console.error('Failed to update policy', err);
      fetchPolicies();
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleCategory = async (classification, category) => {
    const typesInCategory = notificationTypes
      .filter(t => typeToCategory[t.value] === category)
      .map(t => t.value);

    const disabled = getDisabledSet(classification);
    const allDisabled = typesInCategory.every(t => disabled.has(t));

    let newDisabled;
    if (allDisabled) {
      newDisabled = [...disabled].filter(t => !typesInCategory.includes(t));
    } else {
      newDisabled = [...new Set([...disabled, ...typesInCategory])];
    }

    setPolicies(prev =>
      prev.map(p =>
        p.employment_classification === classification
          ? { ...p, disabled_types: newDisabled }
          : p
      )
    );

    const key = `${classification}:${category}-bulk`;
    setSaving(prev => ({ ...prev, [key]: true }));

    try {
      await api.put(`/admin/notification-policies/${classification}/`, {
        disabled_types: newDisabled,
      });
    } catch (err) {
      console.error('Failed to update policy', err);
      fetchPolicies();
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleCategoryExpanded = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const groupedTypes = {};
  notificationTypes.forEach(t => {
    const cat = typeToCategory[t.value] || 'system';
    if (!groupedTypes[cat]) groupedTypes[cat] = [];
    groupedTypes[cat].push(t);
  });

  const categoryOrder = ['attendance', 'leave', 'documents', 'payroll', 'compliance', 'employment', 'nightwork', 'restday_work', 'assets', 'celebrations', 'system'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
        <span className="ml-3 text-gray-600">Loading notification policies...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={fetchPolicies} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Classification Notification Policies
        </h3>
        <p className="text-sm text-gray-500">
          Control which notification types are enabled or disabled per employee classification.
          Disabled types are suppressed for all employees in that classification, overriding individual preferences.
        </p>
      </div>

      {/* Reference panel — collapsible catalogue of all live types */}
      <NotificationTypeReferencePanel
        notificationTypes={notificationTypes}
        typeToCategory={typeToCategory}
        typeToPriority={typeToPriority}
        categoryLabels={CATEGORY_LABELS}
        categoryOrder={categoryOrder}
      />

      {/* Matrix by category */}
      {categoryOrder.map(category => {
        const types = groupedTypes[category];
        if (!types || types.length === 0) return null;
        const isExpanded = expandedCategories[category];
        const label = CATEGORY_LABELS[category] || category;

        return (
          <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Category header */}
            <button
              onClick={() => toggleCategoryExpanded(category)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <h4 className="text-base font-semibold text-gray-900">{label}</h4>
                <CountBadge count={types.length} theme="admin" size="sm" />
              </div>

              {/* Category-level bulk indicators */}
              <div className="flex items-center space-x-4">
                {CLASSIFICATION_ORDER.map(cls => {
                  const disabled = getDisabledSet(cls);
                  const allOff = types.every(t => disabled.has(t.value));
                  const someOff = types.some(t => disabled.has(t.value));
                  const count = classificationCounts[cls] || 0;
                  const statusLabel = allOff ? 'All disabled' : someOff ? 'Partially disabled' : 'All enabled';
                  return (
                    <div key={cls} className="flex flex-col items-center" title={`${CLASSIFICATION_FULL_LABELS[cls]} — ${count} employee${count !== 1 ? 's' : ''} — ${statusLabel}`}>
                      <span className="text-xs font-medium text-gray-500 mb-1">{CLASSIFICATION_LABELS[cls]}</span>
                      <span className={`inline-block w-3 h-3 rounded-full ${
                        allOff ? 'bg-red-400' : someOff ? 'bg-amber-400' : 'bg-green-400'
                      }`} />
                    </div>
                  );
                })}
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-gray-100 overflow-x-auto">
                <table className="table table-fixed w-full min-w-[720px]">
                  <colgroup>
                    <col />
                    <col className="w-[80px]" />
                    <col className="w-[80px]" />
                    <col className="w-[80px]" />
                    <col className="w-[80px]" />
                    <col className="w-[80px]" />
                  </colgroup>
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Notification Type</th>
                      {CLASSIFICATION_ORDER.map(cls => {
                        const count = classificationCounts[cls] || 0;
                        return (
                          <th key={cls} className="table-header-cell text-center">
                            <span
                              className="block"
                              title={`${CLASSIFICATION_FULL_LABELS[cls]} (${count} employee${count !== 1 ? 's' : ''})`}
                            >
                              {CLASSIFICATION_LABELS[cls]}
                            </span>
                            <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">
                              {count} emp
                            </span>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleCategory(cls, category); }}
                              className="text-[10px] font-normal text-red-600 hover:text-red-800 mt-0.5 normal-case tracking-normal"
                              title={types.every(t => getDisabledSet(cls).has(t.value)) ? 'Enable all' : 'Disable all'}
                            >
                              {types.every(t => getDisabledSet(cls).has(t.value)) ? 'Enable all' : 'Disable all'}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {types.map((type) => (
                      <tr key={type.value} className="table-row">
                        <td className="table-cell">
                          <span className="text-sm text-gray-700">{type.label}</span>
                        </td>
                        {CLASSIFICATION_ORDER.map(cls => {
                          const disabled = getDisabledSet(cls);
                          const isEnabled = !disabled.has(type.value);
                          const key = `${cls}:${type.value}`;
                          const isSaving = saving[key];

                          return (
                            <td key={cls} className="table-cell text-center">
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => toggleType(cls, type.value)}
                                  disabled={isSaving}
                                  className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500 ${
                                    isEnabled ? 'bg-green-500' : 'bg-gray-300'
                                  } ${isSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                  title={isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                                >
                                  <span
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                                      isEnabled ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-green-400" /> All enabled
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-amber-400" /> Partially disabled
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full bg-red-400" /> All disabled
          </div>
          <div className="ml-auto text-gray-400">
            Changes take effect immediately (cached for up to 5 minutes)
          </div>
        </div>
      </div>

      {/* Channel Delivery Defaults */}
      <ChannelDeliveryDefaults categoryOrder={categoryOrder} />

      {/* RO Digest Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Reporting Officer Digest Settings</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Control how notifications are batched when delivered to Reporting Officers.
            Digest mode consolidates multiple alerts into a single message, reducing noise
            for ROs who manage large teams. These settings apply system-wide regardless of
            individual employee classifications.
          </p>
        </div>
        <RODigestSettings />
      </div>

      {/* Overtime Planning Digest (Phase 1, read-only status) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Overtime Planning Digest</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Weekly forecast-based 72-hour overtime digest for HOD / Deputy HOD /
            Trade Leader / RO and the HR rollup. Read-only operational status —
            the master kill-switch lives in <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-700 font-mono text-[11px]">.env</code>
            so toggling it leaves a deploy-log audit trail.
          </p>
        </div>
        <OvertimeDigestStatus />
      </div>
    </div>
  );
}
